/**
 * Net Revenue chart (Fan, 2026-08-30: "Add a net revenue chart to the group
 * dashboard (this is after OTA commission and for BR OS, minus the COGS /
 * supplier payment)").
 *
 * Deliberately the same visual grammar as the Booking Volume chart directly
 * above it -- same segments, same colours, same stacking order, same total
 * label riding the topmost non-zero segment, same dashed forecast styling --
 * so the pair reads as one system: what was booked, and what is left of it.
 *
 * Data model (with its own tests) lives in lib/groupNetRevenueChart.js.
 */

import { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LabelList, Text,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCompact, formatCurrencyFull } from '../../lib/chartFormatters';
import { ForecastCaveat } from '../../lib/forecastChartUtils';
import { NET_REVENUE_SEGMENTS, computeNetRevenueYtd } from '../../lib/groupNetRevenueChart';
import { ForecastTotalsFooter } from './ForecastTotalsFooter';

export function NetRevenueChart(props) {
  var chartData = props.chartData;
  var displayCurrency = props.displayCurrency;
  var year = props.year;

  var ytd = useMemo(function () {
    return computeNetRevenueYtd(chartData);
  }, [chartData]);

  if (!chartData || chartData.length === 0) return null;

  var hasForecast = chartData.some(function (d) { return d._isForecast; });
  var lastLabel = NET_REVENUE_SEGMENTS[NET_REVENUE_SEGMENTS.length - 1].label;
  var segmentLabelFormatter = function (v) { return v > 0 ? formatCompact(v, displayCurrency) : ''; };

  return (
    <Card data-testid="net-revenue-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Net Revenue (monthly) {year}</CardTitle>
        <p className="text-xs text-muted-foreground">
          What is left of booking volume: Portfolio after OTA/channel commission,
          BR OS after the supplier payment (COGS). BR OS and Portfolio scopes
          only, not group income.
        </p>
        <ForecastCaveat show={hasForecast} />
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={380}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis
              tickFormatter={function (v) { return formatCompact(v, displayCurrency); }}
              tick={{ fontSize: 11 }}
              width={60}
            />
            <Tooltip
              formatter={function (val, name) { return [formatCurrencyFull(val, displayCurrency), name]; }}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {NET_REVENUE_SEGMENTS.map(function (seg) {
              return (
                <Bar key={seg.key} dataKey={seg.label} stackId="netRevenue" fill={seg.color}
                  radius={seg.label === lastLabel ? [2, 2, 0, 0] : [0, 0, 0, 0]}>
                  <LabelList dataKey={seg.label} position="inside" fontSize={10}
                    formatter={segmentLabelFormatter} fill="#fff" />
                  <LabelList
                    position="top"
                    content={(lp) => {
                      const { x, y, width, index } = lp;
                      const row = chartData[index];
                      if (!row || row._carrier !== seg.key || !row._total) return null;
                      return (
                        <Text x={x + width / 2} y={y - 7} textAnchor="middle"
                          verticalAnchor="end" fontSize={11} fontWeight={700}
                          fill="hsl(var(--foreground))">
                          {formatCompact(row._total, displayCurrency)}
                        </Text>
                      );
                    }}
                  />
                </Bar>
              );
            })}
            {/* Forecast segments: never summed into the actuals bars or the
                actuals total, visually distinct, explicitly labelled. */}
            {NET_REVENUE_SEGMENTS.map(function (seg) {
              return (
                <Bar key={seg.key + '_forecast'} dataKey={seg.label + '_forecast'}
                  name={seg.label + ' (forecast)'} stackId="netRevenue"
                  fill={seg.color} fillOpacity={0.35} stroke={seg.color}
                  strokeDasharray="4 2" strokeWidth={1} radius={[2, 2, 0, 0]}>
                  <LabelList dataKey={seg.label + '_forecast'} position="top" fontSize={10}
                    formatter={segmentLabelFormatter} fill={seg.color} />
                </Bar>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
          <span>YTD Net Revenue:</span>
          {NET_REVENUE_SEGMENTS.map(function (seg) {
            return (
              <span key={seg.key} className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                <span>{seg.label}</span>
                <span className="font-mono font-semibold text-foreground">
                  {formatCompact(ytd[seg.label], displayCurrency)}
                </span>
              </span>
            );
          })}
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-foreground" />
            <span>Combined</span>
            <span className="font-mono font-semibold text-foreground">
              {formatCompact(ytd._combined, displayCurrency)}
            </span>
          </span>
        </div>
        <ForecastTotalsFooter
          data={chartData}
          series={NET_REVENUE_SEGMENTS.map(function (seg) {
            return { key: seg.label, label: seg.label, color: seg.color };
          })}
          currency={displayCurrency}
          label="Net Revenue"
        />
      </CardContent>
    </Card>
  );
}
